import { Add, ArrowBack, Save } from "@mui/icons-material";
import { Button, FormControlLabel, Switch, TablePagination, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { ChangeEvent, FC, Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import EpiCompanyLogo from './EpiCompanyLogo';
import './EpiInspectionConfig.scss';

const DEFAULT_PERIODICITY_DAYS = 30;
const pageSizes = [10, 25, 50];

const EpiInspectionConfig: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [cnpj, setCnpj] = useState('');
	const [adding, setAdding] = useState(false);
	const [search, setSearch] = useState('');
	const [onlyEnabled, setOnlyEnabled] = useState(true);
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(pageSizes[0]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState<number | null>(null);
	const navigate = useNavigate();
	const { showAlert, showError } = useAlert();
	const { verifyPermission, formatDocument } = useUtils();
	const service = new EpiInspectionService();

	const updateCompany = (companyId: number, changes: Partial<EpiCompanyConfigDTO>) => {
		setCompanies(previous => previous.map(item =>
			item.companyId === companyId ? { ...item, ...changes } : item));
	};

	const loadCompanies = async () => {
		const response = await service.getCompanies();
		setCompanies(response?.data || []);
		setLoading(false);
	};

	useEffect(() => {
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'MANAGE_EPI_COMPANIES');
		}
	}, []);

	const filtered = filter();
	const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / size) - 1));
	const visible = filtered.slice(currentPage * size, currentPage * size + size);

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-inspection-config">
				<div className="top">
					<Button
						variant="contained"
						startIcon={<ArrowBack />}
						onClick={() => navigate('/inspecao-epis')}
					>
						Voltar ao painel
					</Button>
					<TextField
						label="Buscar empresa pelo nome"
						id="group-search"
						size="small"
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(0); }}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={onlyEnabled}
								onChange={(e) => { setOnlyEnabled(e.target.checked); setPage(0); }}
							/>
						}
						label="Mostrar somente empresas ativas"
					/>
				</div>
				<div className="adicionar-empresa">
					<TextField
						label="CPF/CNPJ da empresa"
						id="cnpj-empresa-epi"
						size="small"
						inputProps={{ maxLength: 18 }}
						value={cnpj}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							setCnpj(formatDocument(e.target.value.trim()))}
					/>
					<Button
						variant="contained"
						color="success"
						startIcon={<Add />}
						disabled={!cnpj || adding}
						onClick={() => addCompany()}
					>
						Adicionar empresa
					</Button>
				</div>
				{loading
					? getLoading()
					: !filtered.length
						? <div className="sem-execucoes">
							<Typography variant="h6">Nenhuma empresa adicionada ao módulo.</Typography>
						</div>
						: <Fragment>
							<div className="tabela">
								{visible.map((company) => (
									<div className="linha-empresa" key={company.companyId}>
										<div className="company-identity">
											<Typography variant="subtitle1">{company.companyName}</Typography>
											<Typography variant="caption">{company.cnpj}</Typography>
										</div>
										<FormControlLabel
											control={
												<Switch
													color="success"
													checked={company.enabled}
													onChange={(e) => toggle(company, e.target.checked)}
												/>
											}
											label="Habilitada"
										/>
										<TextField
											label="Periodicidade (dias)"
											size="small"
											type="number"
											disabled={!company.enabled}
											value={company.periodicityDays}
											onChange={(e) => updateCompany(company.companyId,
												{ periodicityDays: Number(e.target.value) })}
										/>
										<EpiCompanyLogo
											company={company}
											onChange={(changes) => updateCompany(company.companyId, changes)}
										/>
										<Button
											variant="contained"
											color="success"
											startIcon={<Save />}
											disabled={!company.enabled || saving === company.companyId}
											onClick={() => save(company)}
										>
											Salvar
										</Button>
									</div>
								))}
							</div>
							<div className="paginacao">
								<TablePagination
									component="div"
									rowsPerPageOptions={pageSizes}
									count={filtered.length}
									rowsPerPage={size}
									page={currentPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
									labelRowsPerPage="Registros por página"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</div>
						</Fragment>
				}
			</div>
		</Fragment>
	);

	function filter() {
		let result = companies;
		if (onlyEnabled) {
			result = result.filter(item => item.enabled);
		}
		if (!search) {
			return result;
		}
		return result.filter(item =>
			item.companyName.toUpperCase().includes(search.toUpperCase()));
	}

	async function addCompany() {
		setAdding(true);
		try {
			await service.addCompanyByCnpj(cnpj);
			await loadCompanies();
			setCnpj('');
			setPage(0);
			showAlert('Empresa adicionada. Habilite-a para exibi-la no módulo.', "success");
		} catch (error: any) {
			showError(typeof error?.response?.data === 'string' ? error.response.data : undefined);
		}
		setAdding(false);
	}

	async function toggle(company: EpiCompanyConfigDTO, enabled: boolean) {
		setSaving(company.companyId);
		try {
			const response = await service.saveConfig(company.companyId, {
				enabled: enabled,
				periodicityDays: company.periodicityDays || DEFAULT_PERIODICITY_DAYS
			});
			updateCompany(company.companyId, { ...response.data });
			showAlert(enabled ? 'Empresa habilitada para inspeção de EPIs' : 'Empresa desabilitada', "success");
		} catch (error) {
			showError();
		}
		setSaving(null);
	}

	async function save(company: EpiCompanyConfigDTO) {
		setSaving(company.companyId);
		try {
			const response = await service.saveConfig(company.companyId, {
				enabled: company.enabled,
				periodicityDays: company.periodicityDays
			});
			updateCompany(company.companyId, { ...response.data });
			showAlert('Configuração salva com sucesso', "success");
		} catch (error) {
			showError();
		}
		setSaving(null);
	}
};

export default EpiInspectionConfig;
